import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeOff, TrendingUp, Shield, Lock, Globe, Fingerprint, Smartphone, Loader2, RefreshCw } from 'lucide-react';
import Footer from './Footer';
import ScheduleCBackground from './ScheduleCBackground';
import { FRONTEND_VERSION } from './VersionCheck';

// Biometric data storage helpers
const saveBiometricData = (email, data) => {
  const existing = JSON.parse(localStorage.getItem('biometric_data') || '{}');
  existing[email.toLowerCase()] = data;
  localStorage.setItem('biometric_data', JSON.stringify(existing));
  localStorage.setItem('last_biometric_email', email.toLowerCase());
  console.log('Biometric data saved for:', email.toLowerCase(), data);
};

const getBiometricData = (email) => {
  if (!email) return null;
  const data = JSON.parse(localStorage.getItem('biometric_data') || '{}');
  return data[email.toLowerCase()];
};

const hasBiometricEnabled = (email) => {
  if (!email) return false;
  const data = getBiometricData(email);
  const enabled = data?.enabled === true;
  console.log('hasBiometricEnabled check:', email, 'data:', data, 'enabled:', enabled);
  return enabled;
};

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  const [rememberEmail, setRememberEmail] = useState(false);
  const [supportsBiometric, setSupportsBiometric] = useState(false);
  const [isBiometricLoading, setIsBiometricLoading] = useState(false);
  const [showBiometricSetupDialog, setShowBiometricSetupDialog] = useState(false);
  const [hasBiometricCredentials, setHasBiometricCredentials] = useState(false);

  useEffect(() => {
    // Fetch user's IP address
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setIpAddress(data.ip))
      .catch(() => setIpAddress('Unable to detect'));
    
    // Check if biometric is supported
    const checkBiometricSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          console.log('Biometric support check - available:', available);
          setSupportsBiometric(available);
        } catch (e) {
          console.log('Biometric support check failed:', e);
          setSupportsBiometric(false);
        }
      }
    };
    checkBiometricSupport();
    
    // Load remembered email
    const savedEmail = localStorage.getItem('remembered_email');
    const lastBioEmail = localStorage.getItem('last_biometric_email');
    
    console.log('Login mount - savedEmail:', savedEmail, 'lastBioEmail:', lastBioEmail);
    console.log('biometric_data in localStorage:', localStorage.getItem('biometric_data'));
    
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberEmail(true);
      // Check if this email has biometric enabled
      const hasBio = hasBiometricEnabled(savedEmail);
      console.log('Setting hasBiometricCredentials from savedEmail:', hasBio);
      setHasBiometricCredentials(hasBio);
    }
    
    // Check last biometric email - this takes priority
    if (lastBioEmail && hasBiometricEnabled(lastBioEmail)) {
      console.log('Setting email and biometric from lastBioEmail:', lastBioEmail);
      setEmail(lastBioEmail);
      setHasBiometricCredentials(true);
    }
  }, []);

  // Check biometric when email changes
  useEffect(() => {
    if (email) {
      const emailLower = email.toLowerCase().trim();
      const hasBio = hasBiometricEnabled(emailLower);
      console.log('Email changed to:', emailLower, 'hasBiometric:', hasBio);
      setHasBiometricCredentials(hasBio);
    }
  }, [email]);

  // Setup biometric - optimized for mobile compatibility (Samsung, iOS)
  const setupBiometric = async (userEmail, userDisplayName, authToken = null) => {
    try {
      // Get the token - use passed token, or fall back to stored values
      const token = authToken || localStorage.getItem('token') || window.tempLoginData?.token;
      console.log('setupBiometric starting - token available:', token ? 'Yes (length: ' + token.length + ')' : 'No');
      
      // Check if running in an iframe - WebAuthn may be restricted
      const isInIframe = window.self !== window.top;
      if (isInIframe) {
        toast.error("Please open this app in a full browser window to enable biometrics. Tap the ↗ arrow icon in the preview bar.", { duration: 6000 });
        return false;
      }

      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        toast.error("Biometric login is not supported on this device/browser.");
        return false;
      }

      // Generate random challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Generate a unique user ID based on email
      const encoder = new TextEncoder();
      const emailBytes = encoder.encode(userEmail.toLowerCase());
      const userId = new Uint8Array(16);
      for (let i = 0; i < 16; i++) {
        userId[i] = emailBytes[i % emailBytes.length];
      }

      // Build publicKey options - minimal config for maximum compatibility
      // Avoid residentKey and strict userVerification to prevent Samsung Pass interception
      const publicKeyOptions = {
        challenge: challenge,
        rp: {
          name: "RevTracker"
          // Omit rp.id to use current origin - more compatible with mobile browsers
        },
        user: {
          id: userId,
          name: userEmail,
          displayName: userDisplayName || userEmail.split('@')[0],
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" },   // ES256
          { alg: -257, type: "public-key" }  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "preferred",  // Changed from "required" - allows fallback
          residentKey: "discouraged"       // Changed from "preferred" - prevents passkey/Samsung Pass
        },
        timeout: 120000,
        attestation: "none"
      };

      console.log('WebAuthn create options:', JSON.stringify(publicKeyOptions, (key, value) => 
        value instanceof Uint8Array ? `Uint8Array(${value.length})` : value
      , 2));

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyOptions
      });

      console.log('WebAuthn credential created:', credential ? 'YES' : 'NO');
      if (credential) {
        console.log('Credential ID:', credential.id);
        console.log('Credential rawId byteLength:', credential.rawId?.byteLength);
      }

      if (credential) {
        try {
          // Convert credential ID to base64 for storage
          const rawIdArray = new Uint8Array(credential.rawId);
          
          // Use a chunked approach for base64 encoding to handle large arrays
          let rawIdBase64;
          try {
            // For smaller arrays, direct approach works
            if (rawIdArray.length < 1000) {
              rawIdBase64 = btoa(String.fromCharCode.apply(null, rawIdArray));
            } else {
              // For larger arrays, use chunked approach
              let binary = '';
              for (let i = 0; i < rawIdArray.length; i++) {
                binary += String.fromCharCode(rawIdArray[i]);
              }
              rawIdBase64 = btoa(binary);
            }
          } catch (encodeError) {
            console.error('Base64 encoding error:', encodeError);
            // Fallback to loop-based approach
            let binary = '';
            for (let i = 0; i < rawIdArray.length; i++) {
              binary += String.fromCharCode(rawIdArray[i]);
            }
            rawIdBase64 = btoa(binary);
          }
          
          const credId = credential.id;
          
          console.log('Credential created - rawId length:', rawIdArray.length, 'base64 length:', rawIdBase64.length);

          // Get device info for nickname
          const getDeviceName = () => {
            const ua = navigator.userAgent;
            if (/iPhone/.test(ua)) return 'iPhone';
            if (/iPad/.test(ua)) return 'iPad';
            if (/Android/.test(ua) && /Mobile/.test(ua)) return 'Android Phone';
            if (/Android/.test(ua)) return 'Android Tablet';
            if (/Macintosh/.test(ua)) return 'Mac';
            if (/Windows/.test(ua)) return 'Windows PC';
            return 'Device';
          };

          // Save credential to localStorage ONLY (this is what makes biometric login work)
          saveBiometricData(userEmail.toLowerCase(), {
            enabled: true,
            credentialId: credId,
            rawId: rawIdBase64,
            setupDate: new Date().toISOString(),
            deviceInfo: {
              userAgent: navigator.userAgent,
              platform: navigator.platform
            }
          });

          console.log('Biometric credential saved to localStorage for:', userEmail.toLowerCase());
          toast.success('Biometric login enabled! 🎉');
          setHasBiometricCredentials(true);
          return true;
        } catch (saveError) {
          console.error('Error saving biometric data:', saveError);
          toast.error('Failed to save biometric data');
          return false;
        }
      }
    } catch (error) {
      console.error('Biometric setup failed:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      if (error.name === 'NotAllowedError') {
        // More specific error messages
        if (error.message?.includes('cancelled') || error.message?.includes('canceled')) {
          toast.error('Biometric setup was cancelled by user');
        } else if (error.message?.includes('denied')) {
          toast.error('Biometric permission was denied');
        } else {
          toast.error(`Biometric error: ${error.message || 'Setup was not allowed'}`);
        }
      } else if (error.name === 'InvalidStateError') {
        toast.error('Biometric already registered on this device');
      } else if (error.name === 'SecurityError') {
        toast.error(`Security error: ${error.message}`);
      } else if (error.name === 'AbortError') {
        toast.error('Biometric setup was aborted');
      } else {
        toast.error(`Biometric failed: ${error.name} - ${error.message}`);
      }
      return false;
    }
    return false;
  };

  // Biometric login - optimized for mobile compatibility
  const handleBiometricLogin = async () => {
    let targetEmail = email || localStorage.getItem('last_biometric_email');
    
    if (!targetEmail) {
      toast.error('Please enter your email first');
      return;
    }
    
    // Normalize email to lowercase
    targetEmail = targetEmail.toLowerCase().trim();
    
    console.log('Biometric login attempt for:', targetEmail);

    const userData = getBiometricData(targetEmail);
    console.log('Retrieved biometric data:', userData);
    
    if (!userData?.enabled || !userData?.rawId) {
      toast.error('Biometric not set up for this account. Please set up again.');
      // Clear invalid data
      const biometricData = JSON.parse(localStorage.getItem('biometric_data') || '{}');
      delete biometricData[targetEmail.toLowerCase()];
      localStorage.setItem('biometric_data', JSON.stringify(biometricData));
      setHasBiometricCredentials(false);
      return;
    }

    setIsBiometricLoading(true);

    try {
      // Generate a challenge
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      // Decode the raw ID from base64
      let rawIdArray;
      try {
        rawIdArray = Uint8Array.from(atob(userData.rawId), c => c.charCodeAt(0));
        console.log('Decoded rawId length:', rawIdArray.length);
      } catch (decodeError) {
        console.error('Failed to decode rawId:', decodeError);
        toast.error('Invalid biometric data. Please set up again.');
        setHasBiometricCredentials(false);
        setIsBiometricLoading(false);
        return;
      }

      // Build publicKey options for authentication
      const publicKeyOptions = {
        challenge: challenge,
        allowCredentials: [{
          id: rawIdArray,
          type: 'public-key',
          transports: ['internal', 'hybrid']  // Added 'hybrid' for better compatibility
        }],
        timeout: 120000,
        userVerification: "preferred"
      };

      console.log('WebAuthn get options - credentialId length:', rawIdArray.length);

      // Request biometric authentication (triggers fingerprint/face prompt)
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyOptions
      });

      console.log('Biometric assertion received:', assertion ? 'success' : 'null');

      if (assertion) {
        // Biometric verified! Call backend to get user data
        const biometricLoginUrl = `${API}/auth/biometric-login`;
        console.log('Calling biometric login API:', biometricLoginUrl);
        
        const response = await axios.post(biometricLoginUrl, {
          email: targetEmail
        });

        if (response.data.access_token) {
          // Store token
          localStorage.setItem('token', response.data.access_token);
          
          // Get user data
          const userResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${response.data.access_token}` }
          });
          
          toast.success('Welcome back! 🎉');
          onLogin(response.data.access_token, userResponse.data);
        } else if (response.data.success) {
          // Fallback for simple success response
          toast.success('Biometric login successful!');
          toast.info('Please complete login with password this time.');
        }
      } else {
        toast.error('Biometric verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Biometric login failed:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      if (error.name === 'NotAllowedError') {
        if (error.message?.includes('credentials') || error.message?.includes('match')) {
          toast.error('Biometric credential not recognized. Please set up again.');
          // Clear invalid credential
          const biometricData = JSON.parse(localStorage.getItem('biometric_data') || '{}');
          delete biometricData[targetEmail.toLowerCase()];
          localStorage.setItem('biometric_data', JSON.stringify(biometricData));
          setHasBiometricCredentials(false);
        } else {
          toast.error('Biometric verification was cancelled or denied.');
        }
      } else if (error.name === 'InvalidStateError') {
        toast.error('No matching credential found. Please set up biometrics again.');
        setHasBiometricCredentials(false);
      } else if (error.name === 'SecurityError') {
        toast.error('Security error. Make sure you are using HTTPS.');
      } else {
        toast.error(`Biometric failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsBiometricLoading(false);
    }
  };

  // Handle password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email.toLowerCase().trim(),
        password
      });

      const token = response.data.access_token;
      localStorage.setItem('token', token);
      
      // Handle remember email
      if (rememberEmail) {
        localStorage.setItem('remembered_email', email.toLowerCase().trim());
      } else {
        localStorage.removeItem('remembered_email');
      }

      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = userResponse.data;
      
      // Check if should prompt for biometric setup
      const neverAskList = JSON.parse(localStorage.getItem('biometricNeverAsk') || '[]');
      const emailLower = email.toLowerCase();
      const isInNeverAsk = neverAskList.some(e => e.toLowerCase() === emailLower);
      const shouldAskBiometric = supportsBiometric && 
                                  !hasBiometricEnabled(email) && 
                                  !isInNeverAsk;

      if (shouldAskBiometric) {
        // Store login data temporarily
        window.tempLoginData = { token, user: userData };
        setShowBiometricSetupDialog(true);
      } else {
        toast.success(`Welcome back${userData.display_name ? ', ' + userData.display_name : ''}!`);
        onLogin(token, userData);
      }
    } catch (error) {
      console.error('Login failed:', error);
      // Handle FastAPI validation errors (detail can be an array of objects)
      const detail = error.response?.data?.detail;
      let errorMessage = 'Login failed. Please check your credentials.';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message || 'Validation error').join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupBiometric = async () => {
    setShowBiometricSetupDialog(false);
    
    // Get token from tempLoginData or localStorage
    const token = window.tempLoginData?.token || localStorage.getItem('token');
    console.log('handleSetupBiometric - token available:', token ? 'Yes' : 'No');
    
    const success = await setupBiometric(email, window.tempLoginData?.user?.display_name, token);
    
    // Complete login regardless of biometric setup result
    if (window.tempLoginData) {
      onLogin(window.tempLoginData.token, window.tempLoginData.user);
      window.tempLoginData = null;
    }
  };

  const handleSkipBiometric = () => {
    setShowBiometricSetupDialog(false);
    toast.info('You can enable biometric login later from settings');
    
    if (window.tempLoginData) {
      onLogin(window.tempLoginData.token, window.tempLoginData.user);
      window.tempLoginData = null;
    }
  };

  const handleNeverAskBiometric = () => {
    const neverAskList = JSON.parse(localStorage.getItem('biometricNeverAsk') || '[]');
    if (!neverAskList.includes(email.toLowerCase())) {
      neverAskList.push(email.toLowerCase());
      localStorage.setItem('biometricNeverAsk', JSON.stringify(neverAskList));
    }
    
    setShowBiometricSetupDialog(false);
    toast.info('Biometric prompt disabled. Enable from Settings anytime.');
    
    if (window.tempLoginData) {
      onLogin(window.tempLoginData.token, window.tempLoginData.user);
      window.tempLoginData = null;
    }
  };

  // Show biometric button if credentials exist for any email
  const showBiometricButton = supportsBiometric && hasBiometricCredentials;
  
  // Log for debugging
  console.log('Render - supportsBiometric:', supportsBiometric, 'hasBiometricCredentials:', hasBiometricCredentials, 'showBiometricButton:', showBiometricButton);

  return (
    <div className="min-h-screen flex flex-col bg-slate-950 relative overflow-hidden">
      {/* Neon Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Neon glow orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/30 rounded-full blur-[128px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-[128px] animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-pink-500/20 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/3 right-0 w-80 h-80 bg-blue-500/25 rounded-full blur-[120px] animate-pulse" style={{animationDelay: '0.5s'}}></div>
        
        {/* Neon lines */}
        <div className="absolute top-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        <div className="absolute bottom-40 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
        
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-64 h-64 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl"></div>
      </div>
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4 py-8 relative z-10">
        <div className="w-full max-w-md relative group" data-testid="login-card">
          {/* Outer Glow Effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700"></div>
          
          {/* Animated Neon Border */}
          <div className="absolute -inset-[3px] bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 rounded-xl opacity-90 animate-pulse"></div>
          <div className="absolute -inset-[2px] bg-gradient-to-r from-pink-500 via-cyan-400 to-purple-500 rounded-xl opacity-80 blur-[1px]"></div>
          
          {/* Main Card Container */}
          <Card className="relative bg-slate-900 rounded-xl shadow-2xl border-0 overflow-hidden">
            {/* Inner subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 pointer-events-none"></div>
            <div className="relative">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/40">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">RevTracker</CardTitle>
            <CardDescription className="text-slate-400">
              Professional Financial Management
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Biometric Login Button - AT TOP */}
            {showBiometricButton && (
              <div className="space-y-3">
                <Button
                  type="button"
                  onClick={handleBiometricLogin}
                  disabled={isBiometricLoading}
                  className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-xl shadow-lg shadow-emerald-500/25 border-0"
                  data-testid="biometric-login-button"
                >
                  {isBiometricLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Fingerprint className="w-5 h-5 mr-2" />
                  )}
                  {isBiometricLoading ? 'Verifying...' : 'Login with Biometrics'}
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-900 px-2 text-slate-500">or use password</span>
                  </div>
                </div>
              </div>
            )}

            {/* Password Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="h-12 rounded-xl bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    className="h-12 rounded-xl bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20 pr-12"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberEmail}
                  onCheckedChange={setRememberEmail}
                  className="border-slate-600 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600"
                />
                <Label htmlFor="remember" className="text-sm text-slate-400 cursor-pointer">
                  Remember my email
                </Label>
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-medium rounded-xl shadow-lg shadow-cyan-500/30 border-0 transition-all duration-300"
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Sign In
                  </>
                )}
              </Button>
            </form>

            {/* Learn More Link */}
            <div className="text-center pt-3">
              <a 
                href="/learn-more" 
                className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5"
                data-testid="learn-more-link"
              >
                Want to Learn More?
              </a>
            </div>

            {/* Security Features */}
            <div className="grid grid-cols-3 gap-2 pt-4 border-t border-slate-700/50">
              <div className="text-center p-2">
                <Shield className="w-5 h-5 mx-auto text-cyan-400 mb-1" />
                <span className="text-xs text-slate-500">Secure</span>
              </div>
              <div className="text-center p-2">
                <Globe className="w-5 h-5 mx-auto text-purple-400 mb-1" />
                <span className="text-xs text-slate-500 truncate block">{ipAddress || '...'}</span>
              </div>
              <div className="text-center p-2">
                <Smartphone className="w-5 h-5 mx-auto text-pink-400 mb-1" />
                <span className="text-xs text-slate-500">Mobile Ready</span>
              </div>
            </div>
          </CardContent>
          </div>
        </Card>
      </div>
    </div>

      {/* Biometric Setup Dialog */}
      <Dialog open={showBiometricSetupDialog} onOpenChange={setShowBiometricSetupDialog}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Fingerprint className="w-6 h-6 text-emerald-400" />
              Enable Biometric Login?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Use your fingerprint or face to login faster next time. Your biometric data never leaves your device.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 pt-4">
            <Button
              onClick={handleSetupBiometric}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white border-0"
            >
              <Fingerprint className="w-4 h-4 mr-2" />
              Yes, Enable Biometrics
            </Button>
            
            <Button
              onClick={handleSkipBiometric}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              Not Now
            </Button>
            
            <button
              onClick={handleNeverAskBiometric}
              className="w-full text-sm text-slate-500 hover:text-slate-300 py-2 transition-colors"
            >
              Don&apos;t ask again
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version & Refresh Button */}
      <div className="relative z-10 flex items-center justify-center gap-3 mb-4">
        <span className="text-xs text-slate-500 font-mono bg-slate-800/50 px-3 py-1.5 rounded-full border border-slate-700/50">
          v{FRONTEND_VERSION}
        </span>
        <button
          onClick={() => {
            if ('caches' in window) {
              caches.keys().then(names => names.forEach(name => caches.delete(name)));
            }
            localStorage.setItem('app_version', '');
            window.location.reload(true);
          }}
          className="text-xs text-slate-500 hover:text-blue-400 flex items-center gap-1.5 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
      </div>

      {/* Footer from Settings */}
      <Footer variant="dark" />
    </div>
  );
}
